/** America/New_York (EST/EDT) display helpers for market / backtest UI. */

const ET = 'America/New_York';

function etParts(ts) {
  const d = ts instanceof Date ? ts : new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: ET,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
      .formatToParts(d)
      .filter((p) => p.type !== 'literal')
      .map((p) => [p.type, p.value])
  );
  // Intl can emit hour "24" for midnight in some engines
  if (parts.hour === '24') parts.hour = '00';
  return parts;
}

/** Chart axis: "Jan 15" in ET */
export function formatEtDay(ts) {
  try {
    return new Date(ts).toLocaleDateString('en-US', {
      timeZone: ET,
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

/** Chart tooltip / timeline: "Jan 15, 09:30" in ET (24h) */
export function formatEtTime(ts) {
  try {
    const p = etParts(ts);
    if (!p) return String(ts || '');
    const monthDay = new Date(ts).toLocaleDateString('en-US', {
      timeZone: ET,
      month: 'short',
      day: 'numeric',
    });
    return `${monthDay}, ${p.hour}:${p.minute}`;
  } catch {
    return String(ts || '');
  }
}

/** Tables / logs: "2024-01-15 09:30" or with seconds */
export function formatEtDateTime(ts, { seconds = false } = {}) {
  const p = etParts(ts);
  if (!p) return String(ts || '').replace('T', ' ').slice(0, seconds ? 19 : 16);
  const base = `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
  return seconds ? `${base}:${p.second}` : base;
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Normalize lightweight-charts Time → Date (UTC instant) or business-day parts. */
function chartTimeParts(time) {
  if (time == null) return null;
  if (typeof time === 'number') {
    const d = new Date(time * 1000);
    return Number.isNaN(d.getTime()) ? null : { kind: 'instant', date: d };
  }
  if (typeof time === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(time)) {
    const [year, month, day] = time.split('-').map(Number);
    return { kind: 'business', year, month, day };
  }
  if (typeof time === 'object' && time.year != null && time.month != null && time.day != null) {
    return {
      kind: 'business',
      year: Number(time.year),
      month: Number(time.month),
      day: Number(time.day),
    };
  }
  const d = new Date(time);
  return Number.isNaN(d.getTime()) ? null : { kind: 'instant', date: d };
}

/**
 * lightweight-charts timeScale.tickMarkFormatter — labels in America/New_York.
 * tickMarkType: 0 Year, 1 Month, 2 DayOfMonth, 3 Time, 4 TimeWithSeconds
 */
export function formatEtChartTick(time, tickMarkType) {
  const parts = chartTimeParts(time);
  if (!parts) return null;

  if (parts.kind === 'business') {
    const { year, month, day } = parts;
    if (tickMarkType === 0) return String(year);
    if (tickMarkType === 1) return MONTHS_SHORT[month - 1] || String(month);
    return `${MONTHS_SHORT[month - 1] || month} ${day}`;
  }

  const p = etParts(parts.date);
  if (!p) return null;
  if (tickMarkType === 0) return p.year;
  if (tickMarkType === 1) return MONTHS_SHORT[Number(p.month) - 1] || p.month;
  if (tickMarkType === 2) return `${MONTHS_SHORT[Number(p.month) - 1]} ${Number(p.day)}`;
  if (tickMarkType === 4) return `${p.hour}:${p.minute}:${p.second}`;
  return `${p.hour}:${p.minute}`;
}

/** lightweight-charts localization.timeFormatter — crosshair label in ET. */
export function formatEtCrosshairTime(time) {
  const parts = chartTimeParts(time);
  if (!parts) return '';
  if (parts.kind === 'business') {
    const { year, month, day } = parts;
    return `${MONTHS_SHORT[month - 1]} ${day}, ${year}`;
  }
  const p = etParts(parts.date);
  if (!p) return '';
  return `${MONTHS_SHORT[Number(p.month) - 1]} ${Number(p.day)}, ${p.hour}:${p.minute}:${p.second} ET`;
}

/** Range label: start → end (ET) */
export function formatEtRange(start, end, { intraday = false } = {}) {
  if (!start && !end) return '';
  const fmt = (t) =>
    intraday ? formatEtDateTime(t) : formatEtDateTime(t).slice(0, 10);
  if (start && end) return `${fmt(start)} → ${fmt(end)} ET`;
  return `${fmt(start || end)} ET`;
}
